import { HTMLAttributes, useMemo } from 'react'

import { Button, Heading, Text } from '@hhgtech/hhg-components'
import { Avatar, Carousel } from '@hhgtech/hhg-components/mantine'
import { useIntl } from 'react-intl'

import { getHelloSitesUrl } from 'api/utils/paths'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath } from 'interfaces/constants'
import { Author, HealthTool } from 'interfaces/types'

import { StyledTool, StyledToolCarousel } from './index.styled'

const Tool = ({
  tool,
}: {
  tool: HealthTool & { reviewedBy?: Author; reviewedDate?: string }
}) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const { locale } = useRouterExtend()
  const { title, name, profileUrl, avatar, role } = useMemo(() => {
    if ('undefined' !== typeof tool?.reviewedBy && tool?.reviewedBy) {
      const { role, displayName, profileUrl, avatar } = tool?.reviewedBy

      if (role === 'expert') {
        // Is Expert
        return {
          title: f({
            id: 'articleAuthor.title.expert',
            defaultMessage: 'Expertly reviewed by',
          }),
          name: displayName,
          profileUrl,
          avatar,
          role,
        }
      } else {
        // Is medical
        return {
          title: f({
            id: 'articleAuthor.title.medical',
            defaultMessage: 'Medically reviewed by',
          }),
          name: displayName,
          profileUrl,
          avatar,
          role,
        }
      }
    }
    return {
      title: null,
      name: null,
      profileUrl: null,
      avatar: null,
      role: null,
    }
  }, [tool])

  return (
    <StyledTool>
      <div>
        <div className="wrapper">
          <div className="content">
            <Heading tag="h3" color="#F7F9FC">
              {tool.title}
            </Heading>
            {'undefined' !== typeof tool?.reviewedBy && tool?.reviewedBy && (
              <div className="subtitle">
                <Avatar
                  src={avatar}
                  size={32}
                  withIndicator={!!role}
                  indicatorConfig={
                    role
                      ? {
                          size: 14,
                          offset: 5,
                          label: (
                            <img
                              loading="lazy"
                              width="14"
                              height="14"
                              src={`${assetImagePath}/article-${role}.svg`}
                              alt=""
                            />
                          ),
                        }
                      : undefined
                  }
                />

                <div className="reviewer">
                  {title && (
                    <Text size="p3" color="#F7F9FC" as="span">
                      {title}
                    </Text>
                  )}
                  {name && (
                    <a
                      data-event-category="Reviewer Name"
                      data-event-action={name}
                      data-event-label={`${getHelloSitesUrl(
                        locale,
                      )}${profileUrl}`}
                      href={profileUrl || '#'}
                    >
                      <Text size="p3" color="#F7F9FC" type="semiBold" as="span">
                        {name}
                      </Text>
                    </a>
                  )}
                  {tool.reviewedDate && (
                    <Text size="p3" color="#F7F9FC" as="span">
                      {' '}
                      • {tool.reviewedDate}
                    </Text>
                  )}
                </div>
              </div>
            )}

            {!isMobile && (
              <a
                href={tool.link}
                data-event-category="Health Tool"
                data-event-action="Embedded Calculation Click"
                data-event-label={`${getHelloSitesUrl(locale)}${tool.link}`}
                className="pointer-event-child-none"
              >
                <Button size="lg" color="tertiary" className="button">
                  {f({ id: 'dueDateCard.button' })}
                </Button>
              </a>
            )}
          </div>

          <div className="banner">
            <img
              src={tool.icon || `${assetImagePath}/due-date-card.png?v=1`}
              alt="decorative background for the due date card"
              loading="lazy"
            />
          </div>
        </div>
      </div>
      {isMobile && (
        <a
          href={tool.link}
          data-event-category="Health Tool"
          data-event-action="Embedded Calculation Click"
          data-event-label={tool.link}
          className="pointer-event-child-none"
        >
          <Button size="lg" color="tertiary" className="button" isBlock>
            {f({ id: 'dueDateCard.button' })}
          </Button>
        </a>
      )}
    </StyledTool>
  )
}

type ToolCarouselProps = {
  tools: Array<HealthTool & { reviewedBy?: Author; reviewedDate?: string }>
} & HTMLAttributes<HTMLDivElement>

export const ToolCarousel = ({ tools, ...rest }: ToolCarouselProps) => {
  const isMobile = useIsMobile()
  return (
    <StyledToolCarousel {...rest}>
      {tools && tools.length > 0 && (
        <Carousel
          controlSize={40}
          controlsOffset={40}
          withIndicators={tools.length > 1}
          withControls={!isMobile && tools.length > 1}
          draggable={tools.length > 1}
        >
          {tools.map((tool) => (
            <Carousel.Slide key={tool.id}>
              <Tool tool={tool} />
            </Carousel.Slide>
          ))}
        </Carousel>
      )}
    </StyledToolCarousel>
  )
}
